/* eslint-disable id-length */
/* eslint-disable max-lines-per-function */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { TableUtility } from '@utility/dynamic-table';
import lottie from 'lottie-web';
import PropTypes from 'prop-types';
import { useAuth } from '../../../Hooks/useAuth';
import normalizeTableUtilityResponse from '../../../Utils/tableUtilityResponse';
import EditIcon from '../../../Assets/images/svg/dar_pencil.svg';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import { getRiskRegisterBaseURL } from '../../../Api/request/riskRegister';
import './RiskRegister.scss';

const PAGE_SIZE = 10;
const INITIAL_PAGINATION = { page: 1, limit: PAGE_SIZE, total: 0 };
const baseURL = getRiskRegisterBaseURL();

const EmptyState = React.memo(({ message }) => {
  const [animationData, setAnimationData] = useState(null);
  const animationContainer = useRef(null);

  useEffect(() => {
    let isMounted = true;
    import('../../../Assets/images/json/dar_milestone_details_load.json').then(
      ({ default: data }) => {
        if (isMounted) setAnimationData(data);
      },
    );
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (animationData && animationContainer.current) {
      const anim = lottie.loadAnimation({
        container: animationContainer.current,
        renderer: 'svg',
        loop: true,
        autoplay: true,
        animationData,
      });
      return () => anim.destroy();
    }
    return undefined;
  }, [animationData]);

  return (
    <div className="ms-empty-state-outer">
      <div className="ms-empty-state">
        <div className="ms-empty-state__animation-card">
          <div ref={animationContainer} className="ms-empty-state__animation" />
          <p className="ms-empty-state__text">{message}</p>
        </div>
      </div>
    </div>
  );
});

EmptyState.propTypes = { message: PropTypes.string.isRequired };
EmptyState.displayName = 'EmptyState';

const hasFilterValue = (item) => {
  if (!item || item?.type !== 'filter') return false;
  if (Array.isArray(item?.selectedValues) && item.selectedValues.length > 0) return true;
  if (Array.isArray(item?.range)) return item.range.some(Boolean);
  return Boolean(item?.selectedValue ?? item?.inputValue ?? item?.value);
};

const getAppliedFiltersFromConfiguration = (tableConfiguration = []) => (
  tableConfiguration
    .flatMap((column) => column?.menuItems || [])
    .flatMap((menuItem) => menuItem?.items || [])
    .filter(hasFilterValue)
);

const getRiskExportMeta = (activeTab) => {
  const now = new Date();
  const pad = (num) => num.toString().padStart(2, '0');
  const dateStr = `${pad(now.getDate())}-${pad(now.getMonth() + 1)}-${now.getFullYear()}`;

  if (activeTab === 'issue') {
    return {
      filename: `Issue_Details(${dateStr})`,
      sheetName: 'Issue Details',
      reportTitle: 'Issue Details',
      toolbarSheetName: 'Issue Details',
    };
  }

  if (activeTab === 'infosec') {
    return {
      filename: `Infosec_Risk_Details(${dateStr})`,
      sheetName: 'Infosec Risk Details',
      reportTitle: 'Infosec Risk Details',
      toolbarSheetName: 'Infosec Risk Details',
    };
  }

  return {
    filename: `Risk_Details(${dateStr})`,
    sheetName: 'Risk Details',
    reportTitle: 'Risk Details',
    toolbarSheetName: 'Risk Details',
  };
};

const renderActionCell = (row, activeTab) => {
  const handleEditClick = () => {
    const projectId = row.project_id;
    const dbId = row.db_id;

    window.open(`/delivery/project-details?projectId=${projectId}&editId=${dbId}&editType=${activeTab}`, '_blank');
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'center', width: '100%', height: '20px' }}>
      <button
        type="button"
        onClick={handleEditClick}
        style={{
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          color: '#7a57f7',
          fontSize: '16px',
          padding: '0',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '20px',
          width: '20px',
        }}
        title="Edit"
      >
        <img src={EditIcon} alt="Edit" style={{ width: '20px', height: '20px' }} />
      </button>
    </div>
  );
};

const renderScoreCell = (score, activeTab) => {
  if (score === undefined || score === null || score === '-') {
    return <span>-</span>;
  }

  const numericScore = parseInt(score, 10);
  if (isNaN(numericScore)) {
    const scoreStr = String(score).trim();
    const norm = scoreStr.toUpperCase();
    let color = '#2c2c2c';
    if (norm === 'HIGH') color = '#e30425';
    else if (norm === 'MEDIUM') color = '#e8a700';
    else if (norm === 'LOW') color = '#08b758';
    return <span style={{ color, fontWeight: '600' }}>{norm}</span>;
  }

  let label = 'Low';
  let color = '#2c2c2c';
  const fontWeight = '600';

  if (activeTab === 'infosec') {
    if (numericScore > 296) {
      label = 'High';
      color = '#e30425';
    } else if (numericScore >= 108) {
      label = 'Medium';
      color = '#e8a700';
    } else {
      label = 'Low';
      color = '#2c2c2c';
    }
  } else {
    if (numericScore > 8) {
      label = 'High';
      color = '#e30425';
    } else if (numericScore >= 4) {
      label = 'Medium';
      color = '#e8a700';
    } else {
      label = 'Low';
      color = '#08b758';
    }
  }

  return <span style={{ color, fontWeight }} title={`Score: ${numericScore}`}>{label}</span>;
};

// Single source of truth for all filter fields and their applicable tabs
const FILTER_FIELD_TABS = {
  project_vertical: ['risk', 'issue', 'infosec'],
  project_type: ['risk', 'issue', 'infosec'],
  project_status: ['risk', 'issue', 'infosec'],
  project: ['risk', 'issue', 'infosec'],
  raisedOn: ['risk', 'issue', 'infosec'],
  targetDate: ['risk', 'issue', 'infosec'],
  revisedTargetDate: ['risk', 'issue', 'infosec'],
  lastUpdatedOn: ['risk', 'issue', 'infosec'],

  // Risk specific
  riskId: ['risk'],
  riskCategory: ['risk'],
  riskStatus: ['risk', 'infosec'],
  riskOwner: ['risk'],

  // Shared fields
  score: ['risk', 'infosec'],
  likelihood: ['risk', 'infosec'],
  impact: ['risk', 'infosec'],
  mitigationStrategy: ['risk', 'infosec'],

  // Issue specific
  issueId: ['issue'],
  issueCategory: ['issue'],
  issueStatus: ['issue'],
  priority: ['issue'],
  assignedTo: ['issue'],

  // InfoSec specific
  infosecId: ['infosec'],
  infosecOwner: ['infosec'],
  assetCategory: ['infosec'],

};

const normalizeCapturedFilters = (filters) => {
  if (!Array.isArray(filters)) return [];

  return filters
    .filter((filter) => filter && typeof filter === 'object' && typeof filter.field === 'string')
    .map((filter) => {
      const minimalFilter = { field: filter.field };

      if (Array.isArray(filter.rangePlaceholders) && filter.rangePlaceholders.length > 0) {
        minimalFilter.rangePlaceholders = [...filter.rangePlaceholders];
      }

      if (Array.isArray(filter.range) && filter.range.some((value) => value !== null
      && value !== undefined && value !== '')) {
        minimalFilter.range = [...filter.range];
      }

      if (Array.isArray(filter.selectedValues) && filter.selectedValues.length > 0) {
        minimalFilter.selectedValues = [...filter.selectedValues];
      }

      if (filter.condition !== null && filter.condition !== undefined && filter.condition !== '') {
        minimalFilter.condition = filter.condition;
      }

      if (filter.value !== null && filter.value !== undefined && filter.value !== '') {
        minimalFilter.value = filter.value;
      }

      return minimalFilter;
    });
};

const RiskRegister = () => {
  const { userProfile } = useAuth();
  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const urlProjectId = searchParams.get('projectId');
  const urlTab = searchParams.get('tab');

  const [activeTab, setActiveTab] = useState(() => {
    if (urlTab && ['risk', 'issue', 'infosec'].includes(urlTab.toLowerCase())) {
      return urlTab.toLowerCase();
    }
    return '';
  });
  const [tableScrollHeight, setTableScrollHeight] = useState('500px');
  const [hasAppliedFilters, setHasAppliedFilters] = useState(Boolean(urlProjectId));
  const [appliedFilters, setAppliedFilters] = useState([]);

  // Pagination management matching TimeSheetReportMIS pattern
  const paginationRef = useRef(INITIAL_PAGINATION);
  const [, forceUpdate] = useState(0);
  const triggerPaginationRender = useCallback(() => forceUpdate((n) => n + 1), []);

  const externalFilters = useMemo(
    () => appliedFilters.filter((f) => (FILTER_FIELD_TABS[f.field] || []).includes(activeTab)),
    [appliedFilters, activeTab],
  );

  const externalFiltersRef = useRef(externalFilters);
  useEffect(() => {
    externalFiltersRef.current = externalFilters;
  }, [externalFilters]);

  // Sync activeTab reactively when URL tab parameter changes
  useEffect(() => {
    if (urlTab && ['risk', 'issue', 'infosec'].includes(urlTab.toLowerCase())) {
      const nextTab = urlTab.toLowerCase();
      setActiveTab((prev) => {
        if (prev !== nextTab) {
          paginationRef.current = { ...INITIAL_PAGINATION };
          triggerPaginationRender();
          return nextTab;
        }
        return prev;
      });
    }
  }, [urlTab, triggerPaginationRender]);

  // Sync hasAppliedFilters reactively when URL projectId changes
  useEffect(() => {
    if (urlProjectId) {
      setHasAppliedFilters(true);
    }
  }, [urlProjectId]);

  const userPermissions = useMemo(() => userProfile?.permissions || {}, [userProfile]);

  const hasRiskAccess = useMemo(
    () =>
      Array.isArray(userPermissions['delivery:project-overview:risk'])
      && userPermissions['delivery:project-overview:risk'].some((action) =>
        ['read', 'write', 'update', 'delete', 'create', 'edit'].includes(action.toLowerCase()),
      ),
    [userPermissions],
  );

  const hasIssueAccess = useMemo(
    () =>
      Array.isArray(userPermissions['delivery:project-overview:issue'])
      && userPermissions['delivery:project-overview:issue'].some((action) =>
        ['read', 'write', 'update', 'delete', 'create', 'edit'].includes(action.toLowerCase()),
      ),
    [userPermissions],
  );

  const hasInfoSecAccess = useMemo(
    () =>
      Array.isArray(userPermissions['delivery:project-overview:infosec-risk'])
      && userPermissions['delivery:project-overview:infosec-risk'].some((action) =>
        ['read', 'write', 'update', 'delete', 'create', 'edit'].includes(action.toLowerCase()),
      ),
    [userPermissions],
  );

  useEffect(() => {
    if (!activeTab) {
      if (hasRiskAccess) {
        setActiveTab('risk');
      } else if (hasIssueAccess) {
        setActiveTab('issue');
      } else if (hasInfoSecAccess) {
        setActiveTab('infosec');
      }
    }
  }, [hasRiskAccess, hasIssueAccess, hasInfoSecAccess, activeTab]);

  const authToken = userProfile?.access_token || '';

  const riskUserInfo = useMemo(() => ({
    emailId: userProfile?.email || '',
    permissions: userProfile?.permissions || {},
    roleDetails: userProfile?.roleDetails || [],
    employeeId: userProfile?.employeeId || '',
    globalColors: userProfile?.siteConfiguration?.global_colors || {},
  }), [userProfile]);

  const handlePaginationChange = useCallback((newPagination) => {
    paginationRef.current = newPagination;
    triggerPaginationRender();
  }, [triggerPaginationRender]);

  const handleFilteredApiResponse = useCallback((response) => {
    if (!response) return;

    let rawTableConfiguration = [];
    if (Array.isArray(response.data?.tableConfiguration)) {
      rawTableConfiguration = response.data.tableConfiguration;
    } else if (Array.isArray(response.data?.data?.tableConfiguration)) {
      rawTableConfiguration = response.data.data.tableConfiguration;
    }

    rawTableConfiguration = rawTableConfiguration.map((col) => ({
      ...col,
      menuItems: Array.isArray(col.menuItems) ? col.menuItems : [],
      ...(col.field === 'action' ? {
        className: 'action-column',
        headerClassName: 'action-column',
        bodyClassName: 'action-column',
      } : {}),
    }));

    const rawTableData = response.data?.tableData || response.data?.data?.tableData || [];

    const formattedTableData = rawTableData.map((row) => ({
      ...row,
      ...(row.score !== undefined ? { score: renderScoreCell(row.score, activeTab) } : {}),
      action: renderActionCell(row, activeTab),
    }));

    const tableConfigUpdate = rawTableConfiguration.length > 0
      ? { tableConfiguration: rawTableConfiguration }
      : {};

    response.data = {
      ...response.data,
      ...tableConfigUpdate,
      tableData: formattedTableData,
      data: {
        ...(response.data?.data || {}),
        ...tableConfigUpdate,
        tableData: formattedTableData,
      },
    };

    if (response.data?.meta && response.data.meta.pageSize && !response.data.meta.limit) {
      response.data.meta.limit = response.data.meta.pageSize;
    }

    normalizeTableUtilityResponse(response);

    // Extract pagination metadata dynamically
    const pg = response.data?.pagination
               ?? response.data?.paginationConfig
               ?? response.data?.data?.pagination
               ?? response.data?.data?.paginationConfig
               ?? response.data?.meta;

    const next = {
      page: pg?.page ?? pg?.currentPage ?? paginationRef.current.page,
      limit: pg?.limit ?? pg?.pageSize ?? paginationRef.current.limit,
      total: pg?.total ?? pg?.count ?? pg?.totalRecords ?? rawTableData.length ?? 0,
    };

    const prev = paginationRef.current;
    if (prev.page !== next.page || prev.limit !== next.limit || prev.total !== next.total) {
      paginationRef.current = next;
      triggerPaginationRender();
    } else {
      paginationRef.current = next;
    }
  }, [activeTab, triggerPaginationRender]);

  const handleFilterUpdate = useCallback(({ action, filters }) => {
    if (!activeTab) return;

    if (action === 'reset') {
      paginationRef.current = { ...INITIAL_PAGINATION };
      triggerPaginationRender();
      setAppliedFilters((prev) =>
        prev.filter((f) => !(FILTER_FIELD_TABS[f.field] || []).includes(activeTab)));
      return;
    }

    const normalized = normalizeCapturedFilters(filters);
    const validFields = Object.keys(FILTER_FIELD_TABS)
      .filter((field) => (FILTER_FIELD_TABS[field] || []).includes(activeTab));

    paginationRef.current = { ...paginationRef.current, page: 1 };
    triggerPaginationRender();

    setAppliedFilters((prev) => {
      const next = [...prev];

      normalized
        .filter((f) => validFields.includes(f.field))
        .forEach((incoming) => {
          const hasValue = Boolean(
            (incoming.selectedValues && incoming.selectedValues.length)
          || (incoming.range && incoming.range.some(Boolean))
          || incoming.value,
          );
          if (!hasValue) return;

          const idx = next.findIndex((f) => f.field === incoming.field);
          if (idx >= 0) next[idx] = incoming;
          else next.push(incoming);
        });

      return next;
    });
  }, [activeTab, triggerPaginationRender]);

  const handleTabChange = useCallback((nextTab) => {
    if (nextTab === activeTab) return;

    paginationRef.current = { ...INITIAL_PAGINATION };
    triggerPaginationRender();
    setActiveTab(nextTab);
  }, [activeTab, triggerPaginationRender]);

  const riskExportMeta = useMemo(() => getRiskExportMeta(activeTab), [activeTab]);

  const tableConfiguration = useMemo(() => ({
    generalSettings: {
      enableApiFiltering: true,
      baseURL,
      showToolBar: true,
      showPagination: true,
      showTieredMenu: true,
      headers: { token: authToken },
      userInfo: riskUserInfo,
      apiEndpoints: {
        getAllViews: { endpoint: '', enabled: false },
        getFilteredData: { endpoint: '', enabled: true },
        saveOrUpdateView: { endpoint: 'view', enabled: false },
        deleteView: { endpoint: 'view-delete', enabled: false },
        download: {
          endpoint: 'export',
          enabled: true,
          filename: () => riskExportMeta.filename,
          sheetName: () => riskExportMeta.sheetName,
          reportTitle: () => riskExportMeta.reportTitle,
        },
      },
      handler: {
        handleFilteredApiResponse,
        onFilterUpdate: handleFilterUpdate,
        setTableStateHandler: (getTableState) => {
          const tableState = getTableState?.();
          const configFilters = getAppliedFiltersFromConfiguration(tableState?.tableConfiguration || []);
          const stateFilters = Array.isArray(tableState?.filters)
            ? tableState.filters.filter(hasFilterValue)
            : [];
          const activeAppliedFilters = stateFilters.length > 0 ? stateFilters : configFilters;
          const nextHasApplied = activeAppliedFilters.length > 0 || externalFiltersRef.current.length > 0;
          setHasAppliedFilters((prev) => (prev === nextHasApplied ? prev : nextHasApplied));
        },
      },
      additionalPayloadData: {
        activeTab,
        ...(urlProjectId ? { projectId: urlProjectId } : {}),
      },
      toolBarSettings: {
        showFilterOption: true,
        showColumnFilterMenu: true,
        showAddNew: false,
        showExpandCollapse: false,
        showDownloadExcel: true,
        downloadExcelSheetName: riskExportMeta.toolbarSheetName,
        showColumnCustomize: false,
        showHelp: false,
        showSearch: false,
        showSaveView: false,
        showToolBarHeading: true,
        toolBarHeading: 'Risk Register',
        showReset: true,
        showFilters: true,
        additionalElements: [],
      },
    },
    tableSettings: {
      size: 'small',
      pageSize: PAGE_SIZE,
      tableScrollHeight,
      loaderSkeleton: { rows: 10, bodyCells: 2, columns: 8 },
      paginationConfig: {
        ...paginationRef.current,
        onPaginationChange: handlePaginationChange,
        rowsPerPageOptions: [10, 20, 30, 40],
      },
      showExpandCollapse: false,
      maxFrozenColumns: { desktop: 2, tablet: 2, mobile: 1 },
    },
  }), [
    authToken,
    riskUserInfo,
    activeTab,
    riskExportMeta,
    tableScrollHeight,
    handleFilteredApiResponse,
    handleFilterUpdate,
    handlePaginationChange,
    urlProjectId,
  ]);

  const hasEditAccess = useMemo(() => {
    const misActions = userPermissions['mis:risk-register'] || [];
    const hasMisEdit = misActions.some((act) =>
      ['update', 'write', 'create', 'delete', 'edit'].includes(act.toLowerCase()),
    );
    if (!hasMisEdit) return false;

    if (activeTab === 'risk') {
      const actions = userPermissions['delivery:project-overview:risk'] || [];
      return actions.some((act) => ['update', 'write', 'create', 'delete', 'edit'].includes(act.toLowerCase()));
    }
    if (activeTab === 'issue') {
      const actions = userPermissions['delivery:project-overview:issue'] || [];
      return actions.some((act) => ['update', 'write', 'create', 'delete', 'edit'].includes(act.toLowerCase()));
    }
    if (activeTab === 'infosec') {
      const actions = userPermissions['delivery:project-overview:infosec-risk'] || [];
      return actions.some((act) => ['update', 'write', 'create', 'delete', 'edit'].includes(act.toLowerCase()));
    }
    return false;
  }, [userPermissions, activeTab]);

  // Handle height resize
  useEffect(() => {
    const handleResize = () => {
      setTableScrollHeight(`${Math.floor(window.innerHeight * 0.65)}px`);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <div className={`risk-register-outer-layer ${hasEditAccess ? 'has-action-col' : 'no-action-col'}`}>
      <div className="risk-register-table-container" style={{ position: 'relative' }}>
        {(hasRiskAccess || hasIssueAccess || hasInfoSecAccess) && (
          <div className="risk-tabs-wrapper">
            <div className="risk-tabs-container">
              {hasRiskAccess && (
                <button
                  type="button"
                  className={`risk-tab-btn ${activeTab === 'risk' ? 'active' : ''}`}
                  onClick={() => handleTabChange('risk')}
                >
                  Risk
                </button>
              )}
              {hasIssueAccess && (
                <button
                  type="button"
                  className={`risk-tab-btn ${activeTab === 'issue' ? 'active' : ''}`}
                  onClick={() => handleTabChange('issue')}
                >
                  Issue
                </button>
              )}
              {hasInfoSecAccess && (
                <button
                  type="button"
                  className={`risk-tab-btn ${activeTab === 'infosec' ? 'active' : ''}`}
                  onClick={() => handleTabChange('infosec')}
                >
                  InfoSec Risk
                </button>
              )}
            </div>
          </div>
        )}

        {activeTab ? (
          <div className={`risk-register-table-utility table-utility ${!hasAppliedFilters ? 'risk-no-data' : ''}`}>
            <TableUtility
              key={`risk-register-table-${activeTab}`}
              configuration={tableConfiguration}
              externalFilters={externalFilters}
            />
            {!hasAppliedFilters && (
              <EmptyState message="Select the filters above to see the results" />
            )}
          </div>
        ) : (
          <div style={{ padding: '40px 20px', textAlign: 'center', color: '#666', fontSize: '16px' }}>
            You do not have permission to view any Risk Register reports.
          </div>
        )}
      </div>
    </div>
  );
};

export default RiskRegister;
